import { useState } from "react";
import { ChecklistStep } from "../../components/widgets/ProgressBar";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import CheckboxGroup from "../../components/forms/CheckboxGroup";
import SelectInput from "../../components/forms/SelectInput";
import NotesField from "../../components/forms/NotesField";
import { useStore } from "../../js/state/store";

export default function BuyerExterior() {
  const saved = useStore().state.buyer.current.preferences.exterior || {};
  const [form, setForm] = useState({
    porch: saved.porch || [],
    deck: saved.deck || "",
    patio: saved.patio || "",
    outdoorKitchen: saved.outdoorKitchen || "",
    poolSpa: saved.poolSpa || [],
    solar: saved.solar || "",
    roof: saved.roof || [],
    siding: saved.siding || [],
    windows: saved.windows || [],
    accessibility: saved.accessibility || [],
    notes: saved.notes || "",
  });

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  return (
    <ChecklistStep
      title="Exterior"
      lead="Curb appeal, outdoor living, and the building envelope — roof, siding, windows, and access."
      preferenceKey="exterior"
      value={form}
    >
      <section className="checklist-section">
        <h2>Outdoor Living</h2>
        <ChoiceGroup
          label="Porch"
          name="porch"
          multiple
          values={form.porch}
          onChange={update}
          options={[
            { value: "covered", label: "Covered porch" },
            { value: "uncovered", label: "Uncovered porch" },
            { value: "wrap", label: "Wraparound" },
            { value: "none", label: "Porch not needed" },
          ]}
        />
        <ChoiceGroup
          label="Deck"
          name="deck"
          value={form.deck}
          onChange={update}
          options={[
            { value: "required", label: "Want a deck" },
            { value: "nice", label: "Nice to have" },
            { value: "no", label: "Not needed" },
          ]}
        />
        <SelectInput
          label="Patio size"
          name="patio"
          value={form.patio}
          onChange={update}
          options={[
            { value: "none", label: "No patio needed" },
            { value: "small", label: "Small (bistro / 2 chairs)" },
            { value: "medium", label: "Medium (table for 4–6)" },
            { value: "large", label: "Large entertaining patio" },
            { value: "covered", label: "Covered patio preferred" },
          ]}
        />
        <ChoiceGroup
          label="Outdoor kitchen"
          name="outdoorKitchen"
          value={form.outdoorKitchen}
          onChange={update}
          options={[
            { value: "required", label: "Want one" },
            { value: "nice", label: "Nice to have" },
            { value: "no", label: "Not needed" },
          ]}
        />
        <CheckboxGroup
          label="Pool / spa"
          name="poolSpa"
          values={form.poolSpa}
          onChange={update}
          layout="grid"
          options={[
            { value: "pool", label: "Swimming pool" },
            { value: "spa", label: "Spa / hot tub" },
            { value: "either", label: "Either is a plus" },
            { value: "no", label: "Prefer no pool (maintenance)" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Building Systems</h2>
        <ChoiceGroup
          label="Solar panels"
          name="solar"
          value={form.solar}
          onChange={update}
          options={[
            { value: "have", label: "Want existing solar" },
            { value: "ready", label: "Solar-ready is enough" },
            { value: "no", label: "Not a factor" },
          ]}
        />
        <CheckboxGroup
          label="Roof type"
          name="roof"
          values={form.roof}
          onChange={update}
          layout="grid"
          options={[
            { value: "composition", label: "Composition shingle" },
            { value: "tile", label: "Tile" },
            { value: "metal", label: "Metal" },
            { value: "slate", label: "Slate" },
            { value: "flat", label: "Flat / modern" },
            { value: "any", label: "No roof preference" },
          ]}
        />
        <CheckboxGroup
          label="Siding type"
          name="siding"
          values={form.siding}
          onChange={update}
          layout="grid"
          options={[
            { value: "stucco", label: "Stucco" },
            { value: "fiber-cement", label: "Fiber cement" },
            { value: "wood", label: "Wood / shingle" },
            { value: "brick", label: "Brick" },
            { value: "stone", label: "Stone" },
            { value: "vinyl", label: "Vinyl" },
          ]}
        />
        <CheckboxGroup
          label="Windows"
          name="windows"
          values={form.windows}
          onChange={update}
          layout="grid"
          options={[
            { value: "dual-pane", label: "Dual-pane" },
            { value: "energy", label: "Energy-efficient / Low-E" },
            { value: "triple", label: "Triple-pane a plus" },
            { value: "large", label: "Large / lots of glass" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Exterior Accessibility</h2>
        <CheckboxGroup
          label="Access features at the exterior"
          name="accessibility"
          values={form.accessibility}
          onChange={update}
          layout="grid"
          options={[
            { value: "no-step", label: "No-step / zero-threshold entry" },
            { value: "ramp", label: "Ramp or room for a ramp" },
            { value: "wide-door", label: "Wide front door" },
            { value: "covered-dropoff", label: "Covered drop-off" },
            { value: "path-lighting", label: "Well-lit walkway" },
          ]}
        />
        <NotesField
          label="Exterior notes for my agent"
          name="notes"
          value={form.notes}
          onChange={update}
          placeholder="Curb appeal style, HOA exterior rules, roof age concerns, or outdoor living must-haves."
        />
      </section>
    </ChecklistStep>
  );
}
